import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { organizerContactList } from "../../api";
import ExportToExcel from "../../components/ExportToExcel/ExportToExcel";
import { PulseLoader } from "react-spinners";
import { useAuth } from "../../context/AuthContext";
import { useNightMode } from "../../contexts/NightModeContext";
import Background from "../Login/Background";
import { 
  Users, 
  UserCheck, 
  Mail, 
  Search, 
  Send, 
  CheckSquare, 
  Square,
  RefreshCw
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  RowSelectionState,
} from "@tanstack/react-table";

import PageHeader from "../../components/common/PageHeader/PageHeader";

const ContactList = () => {
  const { slug } = useParams();
  const [contact, setContactList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { user } = useAuth();
  const { isNightMode } = useNightMode();

  const getContactList = async () => {
    try {
      setIsLoading(true);
      if (user) {
        const organizerId = user.organizerId;
        const response = await organizerContactList(organizerId);
        if (response) {
          const seenEmails = new Set<string>();
          const updatedResponse = response.filter((item: any) => {
            const fName = item.firstName?.toLowerCase() || '';
            const lName = item.lastName?.toLowerCase() || '';
            if (fName.includes('attendee') || lName.includes('attendee')) return false;
            if (!item.firstName?.trim() && !item.lastName?.trim() && !item.email?.trim()) return false;
            if (item.email && seenEmails.has(item.email.toLowerCase())) return false;
            if (item.email) seenEmails.add(item.email.toLowerCase());
            return true;
          });

          setContactList(updatedResponse);
        }
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getContactList();
  }, [slug]);

  const getInitials = (firstName?: string, lastName?: string) => {
    const f = firstName ? firstName.charAt(0).toUpperCase() : "";
    const l = lastName ? lastName.charAt(0).toUpperCase() : "";
    return `${f}${l}` || "AU";
  };

  // Define TanStack Table Columns
  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: "fullName",
        header: "FULL NAME",
        accessorFn: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || row.phoneNo || 'Guest Contact',
        cell: ({ row }) => {
          const item = row.original;
          const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || item.phoneNo || 'Guest Contact';
          return (
            <div className="d-flex align-items-center gap-3">
              <div className="contact-avatar">
                {getInitials(item.firstName, item.lastName)}
              </div>
              <div>
                <div className="fw-bold contact-name-text fs-13px">{fullName}</div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "EMAIL ADDRESS",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.email || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: "phoneNo",
        header: "PHONE NO",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.phoneNo || 'N/A'}
          </span>
        ),
      },
      {
        id: "location",
        header: "LOCATION",
        cell: ({ row }) => {
          const item = row.original;
          const locationParts = [item.address, item.city, item.state, item.country].filter(Boolean).join(', ');
          return (
            <span className="contact-sub-text fs-12px">
              {locationParts || 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: "zipCode",
        header: "ZIP CODE",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.zipCode || 'N/A'}
          </span>
        ),
      },
    ],
    []
  );

  // Initialize TanStack Table Instance
  const table = useReactTable({
    data: contact,
    columns,
    state: {
      globalFilter: searchQuery,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setSearchQuery,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row, idx) => row.contactId || row.id || `c-${idx}`,
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  });

  const isAllSelected = table.getIsAllRowsSelected();
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;

  const handleToggleSelectAll = () => {
    table.toggleAllRowsSelected(!isAllSelected);
  };

  const sendEmailToSelected = () => {
    const selectedEmails = selectedRows
      .map(r => r.original.email)
      .filter(e => !!e);

    if (selectedEmails.length === 0) {
      alert("No valid email addresses found.");
      return;
    }

    const mailto = `mailto:?bcc=${selectedEmails.join(",")}&subject=From Eventox&body=Check Out Eventox,`;
    window.location.href = mailto;
  };

  const contactsWithEmailCount = useMemo(
    () => contact.filter(c => !!c.email).length,
    [contact]
  );

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />

      {/* Page Header Component */}
      <PageHeader
        title="Registry Contacts"
        subtitle="Monitor real-time attendee contacts, manage list selections, send broadcast emails, and export contact sheets."
        isLoading={isLoading}
        actions={
          <>
            <ExportToExcel 
              data={contact} 
              fileName="contact_list" 
              buttonText="Export Attendee Sheet (.CSV)"
            />
          </>
        }
      />

      {/* Stat Cards Grid */}
      <div className="contact-stats-grid">
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TOTAL REGISTRY SIZE</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : contact.length} <span className="fs-14px fw-normal contact-sub-text">attendees</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <Users size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">SELECTED CONTACTS</div>
            <div className="contact-stat-value">
              {selectedCount} <span className="fs-14px fw-normal contact-sub-text">selected</span>
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <UserCheck size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">EMAIL DESTINATIONS</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : contactsWithEmailCount} <span className="fs-14px fw-normal contact-sub-text">valid emails</span>
            </div>
          </div>
          <div className="contact-stat-icon green">
            <Mail size={22} />
          </div>
        </div>
      </div>

      {/* Unified Search & Action Bar */}
      <div className="contact-filter-card">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          
          {/* Single Unified Search Bar using TanStack Table Global Filter */}
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Filter by name, email or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Action Buttons: Select All & Send Email */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="contact-action-btn"
              onClick={handleToggleSelectAll}
              disabled={isLoading || contact.length === 0}
            >
              {isAllSelected ? (
                <>
                  <CheckSquare size={16} className="text-pink" /> Unselect All
                </>
              ) : (
                <>
                  <Square size={16} /> Select All
                </>
              )}
            </button>

            {selectedCount > 0 && (
              <button
                type="button"
                className="contact-action-btn primary"
                onClick={sendEmailToSelected}
              >
                <Send size={15} /> Send Email ({selectedCount})
              </button>
            )}
          </div>

        </div>
      </div>

      {/* TanStack Powered Glassmorphic Contacts Table */}
      <div className="contact-table-card">
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
            <PulseLoader color="#EE3F7C" size={14} />
            <span className="fs-12px font-mono contact-sub-text">Loading Attendee Contacts...</span>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="contact-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-5 contact-sub-text fs-13px">
                        No contacts found matching your current search criteria.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} className={row.getIsSelected() ? 'selected' : ''}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* TanStack Table Pagination */}
            {table.getPageCount() > 1 && (
              <div className="d-flex align-items-center justify-content-between p-3 border-top border-white-5 fs-12px">
                <div className="contact-sub-text">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="contact-action-btn py-1 px-3 fs-11px"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="contact-action-btn py-1 px-3 fs-11px"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContactList;
